import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import HomeRoundedIcon from '@mui/icons-material/HomeRounded'
import MarkEmailUnreadRoundedIcon from '@mui/icons-material/MarkEmailUnreadRounded'
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'
import CloudSyncRoundedIcon from '@mui/icons-material/CloudSyncRounded'
import CenterFocusStrongRoundedIcon from '@mui/icons-material/CenterFocusStrongRounded'
import FamilyRestroomRoundedIcon from '@mui/icons-material/FamilyRestroomRounded'
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import { Browser } from '@capacitor/browser'
import { Avatar, IconButton, Pressable, WorkspaceBrandChip } from '../Common'
import type { Workspace } from '../../types/workspace'
import type { WorkspaceRole } from '../../context/WorkspaceContext'
import { useWorkspace } from '../../context/useWorkspace'
import { useFamilyAccess } from '../../hooks/useFamilyAccess'
import { usePushBackHandler } from '../../context/BackStackContext'
import { useNls } from '../../nls/nlsProvider'
import { MemberSystemPulse } from './sidenav/MemberSystemPulse'
import { useMemberOutboxCounts } from '../../hooks/useMemberOutboxCounts'

const MotionPressable = motion.create(Pressable)

const formatRole = (role: WorkspaceRole): string =>
  role === 'READONLY' ? 'Read-only' : role.charAt(0).toUpperCase() + role.slice(1).toLowerCase()

const openExternalUrl = async (url: string) => {
  try {
    await Browser.open({ url })
  } catch {
    if (typeof window !== 'undefined') window.open(url, '_blank', 'noopener,noreferrer')
  }
}

export type MemberMenuDrawerProps = {
  open: boolean
  onClose: () => void
  displayName: string | null
  identity: string | null
  avatarUrl: string | null
  onOpenOverview?: () => void
  onOpenProfile: () => void
  onOpenIncomingInvites: () => void
  onOpenSettings: () => void
  onEnterFocus: () => void
  onSignOut: () => void
  workspaces: Workspace[]
  activeWorkspaceId: string
  activeRole: WorkspaceRole
  onSelectWorkspace: (workspace: Workspace) => void
  onOpenSyncCenter: () => void
  unreadCount?: number
}

export const MemberMenuDrawer = ({
  open,
  onClose,
  displayName,
  identity,
  avatarUrl,
  onOpenOverview,
  onOpenProfile,
  onOpenIncomingInvites,
  onOpenSettings,
  onEnterFocus,
  onSignOut,
  workspaces,
  activeWorkspaceId,
  activeRole,
  onSelectWorkspace,
  onOpenSyncCenter,
  unreadCount = 0,
}: MemberMenuDrawerProps) => {
  usePushBackHandler(onClose, open)
  const { t } = useNls()
  const { getWorkspaceRole } = useWorkspace()
  const { canSwitchToFamily } = useFamilyAccess()
  const outboxCounts = useMemberOutboxCounts()

  const resolveRole = (ws: Workspace): WorkspaceRole => {
    if (ws.id === activeWorkspaceId) return activeRole
    if (getWorkspaceRole) {
      try {
        return getWorkspaceRole(ws.id)
      } catch {
        // fallback to default role
      }
    }
    return 'VIEWER'
  }

  const portalTarget = typeof document === 'undefined' ? null : document.body

  const drawer = (
    <AnimatePresence>
      {open ? (
        <>
          <MotionPressable
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-[color-mix(in_srgb,var(--bg)_85%,transparent)]"
            onClick={onClose}
            aria-label="Close menu"
          />
          {/* eslint-disable-next-line no-restricted-syntax -- member navigation menu drawer uses custom spring motion and is not a Common modal */}
          <motion.aside role="dialog"
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', stiffness: 280, damping: 30 }}
            className="fixed top-0 bottom-0 left-0 z-50 w-[80vw] max-w-[340px] bg-[var(--surface)] border-r border-[var(--border)] shadow-2xl flex flex-col"
            aria-label="Member menu"
            data-testid="member-menu-drawer"
            data-ui-name="UI-MPCAP-MMENU"
          >
            {/* A. User Row (~72px) */}
            <header className="px-5 py-3.5 border-b border-[var(--border)] min-h-[72px] flex items-center justify-between gap-3">
              <Pressable
                onClick={() => {
                  onOpenProfile()
                  onClose()
                }}
                aria-label={`${displayName ?? 'Member'} - Open profile`}
                className="flex items-center gap-3 min-w-0 flex-1 text-left rounded-lg focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[color-mix(in_srgb,var(--accent)_60%,transparent)]"
              >
                <Avatar
                  photoURL={avatarUrl}
                  name={displayName ?? 'Member'}
                  className="h-[42px] w-[42px] rounded-full overflow-hidden flex items-center justify-center shrink-0 bg-[var(--surface-muted)] text-[var(--text-strong)] border border-[var(--border)] font-semibold text-sm"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-[var(--text-strong)] truncate">{displayName ?? 'Member'}</p>
                  {identity ? <p className="text-xs text-[var(--muted)] truncate">{identity}</p> : null}
                </div>
              </Pressable>
              <IconButton
                variant="tertiary"
                size="md"
                onClick={() => {
                  onOpenSettings()
                  onClose()
                }}
                label={t('NLSSideMenuSettings')}
                icon={<SettingsRoundedIcon style={{ fontSize: 20 }} />}
                className="p-2 -mr-1 rounded-lg text-[var(--muted)] hover:text-[var(--text-strong)] hover:bg-[var(--surface-hover)] transition-colors shrink-0"
              />
            </header>
            {/* Scrollable sections */}
            <div className="flex-1 overflow-y-auto overscroll-contain py-2 flex flex-col">
              <div className="px-4 pt-2 pb-3">
                <MemberSystemPulse
                  workspaceCount={workspaces.length}
                  unreadCount={unreadCount}
                  pendingSyncCount={outboxCounts.queued}
                  failedSyncCount={outboxCounts.failed}
                  onOpenSyncCenter={() => {
                    onOpenSyncCenter()
                    onClose()
                  }}
                />
              </div>
              {/* B. Workspaces section */}
              <div className="text-[var(--muted)] text-[11px] font-mono tracking-[0.15em] px-5 pt-4 pb-2">
                WORKSPACES
              </div>
              <div className="rounded-xl border border-[var(--border)] bg-[var(--surface-muted)] mx-4 p-1 flex flex-col gap-1">
                {workspaces.length === 0 ? (
                  <div className="p-2 text-center">
                    <p className="text-[11px] text-[var(--muted)]">
                      No workspaces available.
                    </p>
                  </div>
                ) : (
                  <>
                    {workspaces.map((ws) => {
                      const isActive = ws.id === activeWorkspaceId
                      const wsRole = resolveRole(ws)
                      return (
                        <Pressable
                          key={ws.id}
                          onClick={() => {
                            onSelectWorkspace(ws)
                            onClose()
                          }}
                          className={`flex items-center gap-3 h-11 rounded-lg px-3 transition-colors ${
                            isActive ? 'bg-[var(--accent-soft)]' : 'hover:bg-[var(--surface-hover)]'
                          }`}
                        >
                          {ws.branding?.logoUrl ? (
                            <WorkspaceBrandChip
                              name={ws.name}
                              logoUrl={ws.branding?.logoUrl}
                              accentColor={ws.branding?.accentColor}
                              fallback="none"
                              size={24}
                            />
                          ) : null}
                          <span className="text-sm text-[var(--text-strong)] truncate flex-1 text-left font-medium">
                            {ws.name}
                          </span>
                          <span className="text-[11px] text-[var(--muted)] shrink-0">
                            {formatRole(wsRole)}
                          </span>
                        </Pressable>
                      )
                    })}
                  </>
                )}
              </div>
              {/* C. This workspace section */}
              <div className="pt-2">
                <div className="text-[var(--muted)] text-[11px] font-mono tracking-[0.15em] px-5 pt-4 pb-2">
                  THIS WORKSPACE
                </div>
                <div className="flex flex-col">
                  {onOpenOverview ? (
                    <Pressable
                      onClick={() => {
                        onOpenOverview()
                        onClose()
                      }}
                      className="w-full flex items-center gap-3 px-5 h-10 text-sm text-[var(--text-strong)] hover:bg-[var(--surface-hover)] transition-colors text-left"
                    >
                      <HomeRoundedIcon style={{ fontSize: 20 }} className="text-[var(--muted)] shrink-0" />
                      <span>Overview</span>
                    </Pressable>
                  ) : null}
                  <Pressable
                    onClick={() => {
                      onOpenIncomingInvites()
                      onClose()
                    }}
                    className="w-full flex items-center gap-3 px-5 h-10 text-sm text-[var(--text-strong)] hover:bg-[var(--surface-hover)] transition-colors text-left"
                  >
                    <MarkEmailUnreadRoundedIcon style={{ fontSize: 20 }} className="text-[var(--muted)] shrink-0" />
                    <span>Incoming invites</span>
                  </Pressable>
                  {canSwitchToFamily(activeWorkspaceId) ? (
                    <Pressable
                      onClick={() => {
                        onClose()
                        if (typeof window !== 'undefined') {
                          window.history.replaceState({ fromWorkspaceId: activeWorkspaceId }, '', '/family')
                          window.dispatchEvent(new PopStateEvent('popstate'))
                        }
                      }}
                      className="w-full flex items-center gap-3 px-5 h-10 text-sm text-[var(--text-strong)] hover:bg-[var(--surface-hover)] transition-colors text-left"
                    >
                      <FamilyRestroomRoundedIcon style={{ fontSize: 20 }} className="text-[var(--muted)] shrink-0" />
                      <span>{t('NLSMemberMenuFamilyView')}</span>
                    </Pressable>
                  ) : null}
                  <Pressable
                    onClick={() => {
                      onOpenSettings()
                      onClose()
                    }}
                    className="w-full flex items-center gap-3 px-5 h-10 text-sm text-[var(--text-strong)] hover:bg-[var(--surface-hover)] transition-colors text-left"
                  >
                    <SettingsRoundedIcon style={{ fontSize: 20 }} className="text-[var(--muted)] shrink-0" />
                    <span>{t('NLSSideMenuSettings')}</span>
                  </Pressable>
                  <Pressable
                    onClick={() => {
                      onOpenSyncCenter()
                      onClose()
                    }}
                    className="w-full flex items-center gap-3 px-5 h-10 text-sm text-[var(--text-strong)] hover:bg-[var(--surface-hover)] transition-colors text-left"
                  >
                    <CloudSyncRoundedIcon style={{ fontSize: 20 }} className="text-[var(--muted)] shrink-0" />
                    <span>Sync center</span>
                  </Pressable>
                  <Pressable
                    onClick={() => {
                      onEnterFocus()
                      onClose()
                    }}
                    className="w-full flex items-center gap-3 px-5 h-10 text-sm text-[var(--text-strong)] hover:bg-[var(--surface-hover)] transition-colors text-left"
                  >
                    <CenterFocusStrongRoundedIcon style={{ fontSize: 20 }} className="text-[var(--muted)] shrink-0" />
                    <span>Focus mode</span>
                  </Pressable>
                </div>
              </div>
            </div>
            {/* D. Footer section */}
            <footer className="mt-auto border-t border-[var(--border)] py-2 flex flex-col">
              <Pressable
                onClick={() => {
                  void openExternalUrl('https://timelining.app/support')
                  onClose()
                }}
                className="w-full flex items-center gap-3 px-5 h-10 text-sm text-[var(--text-strong)] hover:bg-[var(--surface-hover)] transition-colors text-left"
              >
                <HelpOutlineRoundedIcon style={{ fontSize: 20 }} className="text-[var(--muted)] shrink-0" />
                <span>Help & support</span>
              </Pressable>
              <Pressable
                onClick={() => { void openExternalUrl('https://timelining.app/about'); onClose() }}
                className="w-full flex items-center gap-3 px-5 h-10 text-sm text-[var(--text-strong)] hover:bg-[var(--surface-hover)] transition-colors text-left"
              >
                <InfoOutlinedIcon style={{ fontSize: 20 }} className="text-[var(--muted)] shrink-0" />
                <span>About Timelining</span>
              </Pressable>
              <div className="my-1 border-t border-[var(--border)]" />
              <Pressable
                onClick={() => {
                  onSignOut()
                  onClose()
                }}
                className="w-full flex items-center gap-3 px-5 h-10 text-sm text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))] hover:bg-[var(--danger-soft)] transition-colors text-left"
              >
                <LogoutRoundedIcon
                  style={{ fontSize: 20 }}
                  className="text-[var(--danger)] shrink-0" // mosaic-keep: icon
                />
                <span>Sign out</span>
              </Pressable>
            </footer>
          </motion.aside>
        </>
      ) : null}
    </AnimatePresence>
  )
  if (!portalTarget) return drawer
  return createPortal(drawer, portalTarget)
}
